import { canonicalCells } from "../document/footprint-cells.ts";
import { FOOTPRINT_DEFAULT_CELL } from "./footprint-cell.ts";

/**
 * The read-time conversion of a footprint entry's old depth into marks (ADR-132 decision 8). ADR-131's `walkOn`
 * rectangles and ADR-098's `floor` rectangles become `front` cells, `walkUnder` rectangles become `behind` cells,
 * and ADR-098's `lines` are dropped, since a line has no equivalent. A blocked cell keeps its collision and takes no
 * mark. This module reads the old depth as untyped data and imports nothing of the code that drew it.
 */

type Cell = readonly [number, number];

/**
 * What a conversion answers: the entry in the new shape and whether it dropped ADR-098 lines.
 */
export interface ConvertedEntry {
  readonly entry: Readonly<Record<string, unknown>>;
  readonly droppedLines: boolean;
}

/**
 * Reads a list of rectangles `[column, rowUp, columns, rows]` of whole numbers.
 *
 * @param value The value.
 * @returns The rectangles, or null when the value is no such list.
 */
function boxesOf(value: unknown): readonly (readonly [number, number, number, number])[] | null {
  if (!Array.isArray(value) || !value.every((box) => Array.isArray(box) && box.length === 4 && box.every((n) => typeof n === "number" && Number.isInteger(n)))) {
    return null;
  }
  return value as readonly (readonly [number, number, number, number])[];
}

/**
 * Whether a value is a list of cells.
 *
 * @param value The value.
 * @returns True for `[[column, rowUp], ...]`.
 */
function isCells(value: unknown): value is readonly Cell[] {
  return Array.isArray(value) && value.every((cell) => Array.isArray(cell) && cell.length === 2 && cell.every((n) => typeof n === "number" && Number.isInteger(n)));
}

/**
 * Every cell of some rectangles, counted in a finer cell.
 *
 * @param boxes The rectangles.
 * @param factor How many finer cells fit along one side of the rectangles' cell.
 * @returns The cells.
 */
function cellsOfBoxes(boxes: readonly (readonly [number, number, number, number])[], factor: number): Cell[] {
  const cells: Cell[] = [];
  for (const [column, row, columns, rows] of boxes) {
    for (let y = row * factor; y < (row + rows) * factor; y += 1) {
      for (let x = column * factor; x < (column + columns) * factor; x += 1) {
        cells.push([x, y]);
      }
    }
  }
  return cells;
}

/**
 * A cell list counted in a finer cell: each cell becomes the `factor` by `factor` cells it covers.
 *
 * @param cells The cells.
 * @param factor The finer cell's count per side.
 * @returns The cells.
 */
function finer(cells: readonly Cell[], factor: number): Cell[] {
  return factor === 1 ? cells.map((c) => [c[0], c[1]]) : cellsOfBoxes(cells.map((c) => [c[0], c[1], 1, 1] as const), factor);
}

/**
 * Converts one entry's old depth into marks. An entry with no `depth` comes back as the same object. A depth is
 * counted in its own `cell` (8 or 4 pixels, 8 when absent) and the entry's lists in the entry's; when the two
 * differ the finer cell is used for the entry's `cells`, `passage` and marks together, so one cell size measures the
 * whole entry. A depth of a shape this converter does not know (a rectangle list that is no list, a cell that is no
 * 4 or 8) is carried as it is for the validator to name. Existing `behind` and `front` cells are kept; a cell in the
 * blocked `cells` takes no mark, and one under both kinds stays `front`.
 *
 * @param entry The stored entry, an object.
 * @returns The converted entry, with its fields otherwise as they were, and whether lines were dropped.
 */
export function convertLegacyDepth(entry: Readonly<Record<string, unknown>>): ConvertedEntry {
  const { depth, ...rest } = entry;
  if (depth === undefined) {
    return { entry, droppedLines: false };
  }
  if (typeof depth !== "object" || depth === null || Array.isArray(depth)) {
    return { entry, droppedLines: false };
  }
  const old = depth as Record<string, unknown>;
  const kinds = [old["walkOn"], old["walkUnder"], old["floor"]];
  const listed = kinds.map((value) => (value === undefined ? [] : boxesOf(value)));
  const cellOk = old["cell"] === undefined || old["cell"] === 8 || old["cell"] === 4;
  if (!cellOk || listed.some((boxes) => boxes === null) || (rest["cells"] !== undefined && !isCells(rest["cells"])) || (rest["passage"] !== undefined && !isCells(rest["passage"])) || (rest["front"] !== undefined && !isCells(rest["front"])) || (rest["behind"] !== undefined && !isCells(rest["behind"]))) {
    return { entry, droppedLines: false };
  }
  const [walkOn, walkUnder, floor] = listed as [readonly (readonly [number, number, number, number])[], readonly (readonly [number, number, number, number])[], readonly (readonly [number, number, number, number])[]];
  const entryCell = typeof rest["cell"] === "number" ? rest["cell"] : FOOTPRINT_DEFAULT_CELL;
  const depthCell = typeof old["cell"] === "number" ? old["cell"] : FOOTPRINT_DEFAULT_CELL;
  const target = Math.min(entryCell, depthCell);
  const entryFactor = entryCell / target;
  const depthFactor = depthCell / target;
  const lift = (cells: unknown): Cell[] => finer(cells as readonly Cell[], entryFactor);
  const blocked = new Set((rest["cells"] === undefined ? [] : lift(rest["cells"])).map((c) => `${c[0]},${c[1]}`));
  const free = (cells: readonly Cell[]): Cell[] => cells.filter((c) => !blocked.has(`${c[0]},${c[1]}`));
  const frontCells = free([...(rest["front"] === undefined ? [] : lift(rest["front"])), ...cellsOfBoxes(walkOn, depthFactor), ...cellsOfBoxes(floor, depthFactor)]);
  const frontKeys = new Set(frontCells.map((c) => `${c[0]},${c[1]}`));
  const behindCells = free([...(rest["behind"] === undefined ? [] : lift(rest["behind"])), ...cellsOfBoxes(walkUnder, depthFactor)]).filter((c) => !frontKeys.has(`${c[0]},${c[1]}`));
  const out: Record<string, unknown> = { ...rest };
  if (entryFactor !== 1) {
    out["cell"] = target;
    for (const key of ["cells", "passage"]) {
      if (rest[key] !== undefined) {
        out[key] = canonicalCells(lift(rest[key]));
      }
    }
  }
  delete out["front"];
  delete out["behind"];
  if (frontCells.length > 0) {
    out["front"] = canonicalCells(frontCells);
  }
  if (behindCells.length > 0) {
    out["behind"] = canonicalCells(behindCells);
  }
  const lines = old["lines"];
  return { entry: out, droppedLines: Array.isArray(lines) ? lines.length > 0 : lines !== undefined };
}
